import React, { useEffect, useState } from "react";
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import Measurements from "./measurements/measurements";
import { useDispatch, useSelector } from "react-redux";
import { getAthleteProfile } from "../../../services/AthleteService";
import { useAxiosInstance } from "../../../services/Interceptor";
import Results from "./combineResults/results";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faUpload,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import {
  updateJerseyNumber,
  updateProfilePhoto,
} from "../../../services/AdminService";
import { updateJerseyNumberAction } from "../../../store/slices/eventSlice.jsx";
import { toast } from "react-toastify";
import PositionDrills from "./position-drills/positionDrills.jsx";
import CopyUrl from "../../../components/athlete-scores/share-profile/copy-url";
import { useResponsiveHeight } from "../../../hooks/useResponsiveHeight.js";

export default function CombinesDetails({
  backButtonClickEvent,
  updateProfilePicture,
}) {
  const dispatch = useDispatch();
  const r_event = useSelector((state) => state.events.attendees);
  const { userAthlete, eventId } = r_event;
  const apiInstance = useAxiosInstance();
  const [athleteData, setAthleteData] = useState();

  const [jerseyNumber, setJerseyNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingPhoto, setLoadingPhoto] = useState(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const { adjustedHeight } = useResponsiveHeight({
    mobileOffset: 420,
    tabletOffset: 390,
    desktopOffset: 380,
  });

  useEffect(() => {
    const getProfile = async () => {
      try {
        const response = await getAthleteProfile(
          apiInstance,
          userAthlete.userId,
        );
        setAthleteData(response);
        setJerseyNumber(response.jerseyNumber || "");
      } catch (error) {
        console.error(error);
      }
    };
    if (userAthlete && userAthlete.userId) {
      getProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAthlete]);

  const validateFile = (file) => {
    const permittedExtensions = [
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
      ".mp4",
      ".mov",
    ];
    const permittedMimeTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "video/mp4",
      "video/quicktime",
    ];
    const maxFileSize = 100 * 1024 * 1024; // 100 MB

    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();
    const isExtensionValid = permittedExtensions.includes(fileExtension);
    const isMimeTypeValid = permittedMimeTypes.includes(file.type);
    const isSizeValid = file.size <= maxFileSize;

    if (!isExtensionValid) {
      toast.error(
        "Invalid file extension. Only JPG, PNG, GIF, MP4, and MOV are allowed.",
        { position: "top-right" },
      );
      return false;
    }

    if (!isMimeTypeValid) {
      toast.error("Invalid file type. Please upload an allowed file format.", {
        position: "top-right",
      });
      return false;
    }

    if (!isSizeValid) {
      toast.error("File size exceeds the 100 MB limit.", {
        position: "top-right",
      });
      return false;
    }

    return true;
  };

  const isJerseyNumberInUse = (number) => {
    return (r_event.eventAttendees ?? []).some(
      (athlete) =>
        athlete.jerseyNumber &&
        athlete.jerseyNumber.toString() === number.toString() &&
        athlete.userId !== userAthlete.userId,
    );
  };
  const handleJerseyNumberUpdate = async () => {
    setLoading(true);

    try {
      const trimmedJerseyNumber =
        jerseyNumber.trim() === "" ? null : jerseyNumber.trim();
      if (trimmedJerseyNumber && isJerseyNumberInUse(trimmedJerseyNumber)) {
        toast.error("This jersey number is already in use for this event.");
        setLoading(false);
        return;
      }

      await updateJerseyNumber(
        apiInstance,
        eventId,
        userAthlete.userId,
        trimmedJerseyNumber,
      );

      toast.success(
        trimmedJerseyNumber === null
          ? "Jersey number successfully removed."
          : "Jersey number successfully updated.",
      );

      updateProfilePicture();

      dispatch(
        updateJerseyNumberAction({
          userId: userAthlete.userId,
          jerseyNumber: trimmedJerseyNumber,
        }),
      );

      setAthleteData((prevData) => ({
        ...prevData,
        jerseyNumber: trimmedJerseyNumber,
      }));
    } catch (errorMessage) {
      toast.error(errorMessage, { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  const handleJerseyNumberChange = (e) => {
    setJerseyNumber(e.target.value);
  };

  const handlePhotoUpload = async () => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*";
    // iOS Safari requires the input to be in the DOM to reliably fire change
    // events. Without this, uploads silently fail after ~3 uses.
    fileInput.style.display = "none";
    document.body.appendChild(fileInput);

    fileInput.onchange = async (event) => {
      document.body.removeChild(fileInput);
      const selectedFile = event.target.files[0];
      if (!selectedFile) {
        toast.error("Please select a photo to upload.");
        return;
      }
      if (!validateFile(selectedFile)) {
        return;
      }

      setLoadingPhoto(true);
      try {
        await updateProfilePhoto(apiInstance, userAthlete.userId, selectedFile);
        toast.success("Athlete photo updated successfully!");
        const updatedProfile = await getAthleteProfile(
          apiInstance,
          userAthlete.userId,
        );
        updateProfilePicture();
        setAthleteData(updatedProfile);
      } catch (error) {
        console.error("Error updating profile photo:", error);
        toast.error("Failed to update athlete photo.");
      } finally {
        setLoadingPhoto(false);
      }
    };

    // Clean up if the user dismisses the picker without selecting a file
    fileInput.addEventListener("cancel", () => {
      if (document.body.contains(fileInput)) {
        document.body.removeChild(fileInput);
      }
    });

    fileInput.click();
  };

  return eventId && userAthlete && athleteData ? (
    <div className="bg-white p-4 border-gray-300 rounded-md shadow-lg">
      {loadingPhoto && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/50">
          <FontAwesomeIcon
            icon={faSpinner}
            spin
            size="3x"
            className="text-white"
          />
          <p className="mt-4 text-white text-lg font-medium">
            Uploading photo…
          </p>
        </div>
      )}
      <div className="athletes-list h-full event_details_mob md:absolute xl:relative lg:relative md:col-span-7 xl:col-span-7 xl:space-y-0 xl:bg-transparent xl:border-gray-300 p-0 md:space-y-0 md:top-0 xl:top-0 lg:top-0">
        <div className="text-blue-600 pt-6 px-6 md:visible xl:hidden lg:hidden sm:visible">
          <NavLink onClick={() => backButtonClickEvent(false)}>
            <FontAwesomeIcon icon={faChevronLeft} /> Back
          </NavLink>
        </div>
        <div className="p-4 xl:p-0 lg:p-0">
          <div className="flex justify-between items-center pb-4 mb-1">
            <div className="flex flex-row text-xl uppercase flex-1">
              <div className="relative  my-auto rounded-lg">
                <img
                  alt="Athlete Name"
                  src={athleteData.profilePicture || "/default-profile-img.png"}
                  className="grow-0 shrink-0 rounded-lg object-cover h-[110px] w-[100px]"
                />

                <button
                  onClick={handlePhotoUpload}
                  className="absolute bottom-[-10px] right-0 bg-white shadow-lg rounded-full border-gray-300 text-blue-500 text-sm  p-2 px-3"
                  disabled={loadingPhoto}
                >
                  <FontAwesomeIcon icon={faUpload} alt=" Upload photos" />
                </button>
              </div>
              <div className="ml-4">
                <p className="text-xl text-red-500 uppercase">
                  <span>
                    {athleteData.firstName} {athleteData.lastName} (
                    {athleteData.age})
                  </span>
                  <CopyUrl athleteProfileId={athleteData.id} />
                </p>
                <div className="block">
                  <p className="text-base uppercase font-light">
                    {athleteData.position}
                  </p>
                  <p className="text-base uppercase font-light">
                    {athleteData.collegeSchool?.trim()
                      ? athleteData.collegeSchool
                      : athleteData.highSchool}
                  </p>
                  <p className="text-base uppercase flex flex-col">
                    <span>{athleteData.primaryPosition}</span>
                    <span>{athleteData.secondaryPosition}</span>
                  </p>
                  <p className="text-base uppercase font-light">
                    FLX ID: {athleteData.flxId?.trim()}
                  </p>
                </div>
              </div>
            </div>
            <div className="my-auto h-[70px] w-[70px] bg-[#363636] text-center text-white rounded-md">
              <p className="my-auto text-4xl mt-4">
                {userAthlete.positionScore}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 mb-4">
            <input
              type="text"
              value={jerseyNumber}
              onChange={handleJerseyNumberChange}
              placeholder={
                !userAthlete.jerseyNumber ||
                userAthlete.jerseyNumber.trim() === "" ||
                userAthlete.jerseyNumber === "null"
                  ? "Add jersey number"
                  : userAthlete.jerseyNumber
              }
              className="border rounded-md p-2"
            />

            <button
              onClick={handleJerseyNumberUpdate}
              className={`rounded-md p-2 ${loading ? "bg-[#5057FF]/50 cursor-not-allowed" : "bg-[#5057FF]"} text-white`}
              disabled={loading}
            >
              {" "}
              {loading ? "Adding..." : "Add"}{" "}
            </button>
          </div>

          <TabGroup
            selectedIndex={selectedTabIndex}
            onChange={setSelectedTabIndex}
          >
            <TabList className="flex gap-0 border-b-2 border-neutral-700 ">
              <Tab className="pb-2 px-2 xl:px-4 lg:px-4 font-medium whitespace-nowrap text-sm xl:text-base text-gray-800 focus:outline-none data-[selected]:text-red-500">
                Combine Results
              </Tab>
              <Tab className="pb-2 px-2 xl:px-4 lg:px-4 whitespace-nowrap text-gray-700 focus:outline-none data-[selected]:text-red-500">
                Measurements
              </Tab>
              <Tab className="pb-2 px-2 xl:px-4 lg:px-4 whitespace-nowrap  text-gray-700 focus:outline-none data-[selected]:text-red-500">
                Position Drills
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <div
                  className="mt-4 pr-4 -mr-4 overflow-auto pb-4"
                  style={{ height: `${adjustedHeight}px` }}
                >
                  <Results eventId={eventId} athlete={userAthlete} />
                </div>
              </TabPanel>
              <TabPanel>
                <div
                  className="mt-4 pr-4 -mr-4 overflow-auto"
                  style={{ height: `${adjustedHeight}px` }}
                >
                  <Measurements eventId={eventId} athlete={userAthlete} />
                </div>
              </TabPanel>
              <TabPanel>
                <div
                  className="mt-4 pr-4 -mr-4 overflow-auto"
                  style={{ height: `${adjustedHeight}px` }}
                >
                  <PositionDrills
                    eventId={eventId}
                    athleteId={userAthlete.userId}
                    isActiveTab={selectedTabIndex === 2}
                  />
                </div>
              </TabPanel>
            </TabPanels>
          </TabGroup>
        </div>
      </div>
    </div>
  ) : (
    <div className="h-full flex items-center justify-center">
      <div className=" w-full flex flex-col items-center">
        <img
          alt="Athlete Name"
          src="/select_row.svg"
          className="object-cover h-20 ml-18 pb-3"
        ></img>
        <p className="text-center text-[#8E8E93] text-1xl">
          Select an event and attendee to start!
        </p>
      </div>
    </div>
  );
}
